import {
  ButtonBase,
  Divider,
  Fab,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core';
import clsx from 'clsx';
import { colors } from 'design-system';
import React from 'react';
import { Bell, LogOut, Search, User } from 'react-feather';
import { InformationPopper } from 'src/components/General';
import { getLoggedRole } from 'data-layer-cr/dist/authentication';

import TooltipCustom from '../../../General/TooltipCustom';
import makeDashboardToolbarStyles from './makeDashboardToolbarStyles';

type ClassesType = ReturnType<typeof makeDashboardToolbarStyles>;

/**
 * Makes the ASSA logo for the desktop layout
 *
 * @param classes - Classes generated by useStyles
 */
export function makeDesktopAssaLogo(classes: ClassesType): React.ReactNode {
  return (
    <img
      className={classes.toolbarItem}
      src="/svg/white_assa_logo.svg"
      alt="logo"
      width={130}
    />
  );
}

/**
 * Makes the Search layout
 *
 * @param searchString - The current search string
 * @param onUpdateSearchString - Callback when the search string changes
 * @param classes - Classes generated by useStyles
 */
export function makeSearchDesktop(
  searchString: string,
  onUpdateSearchString: (newSearchString: string) => void,
  classes: ClassesType,
): React.ReactNode {
  return (
    <TooltipCustom title="Puedes buscar por nombre y apellido, por # de referencia o por usuario">
      <div className={clsx(classes.searchContainer, classes.toolbarItem)}>
        <input
          id="sales-web-input-search"
          data-testid="search-input"
          type="text"
          className={classes.searchInput}
          placeholder="Buscar..."
          value={searchString}
          onChange={(e): void => onUpdateSearchString(e.target.value)}
        />
        <Search
          color={colors.bright_turquoise}
          className={classes.searchInputIcon}
        />
      </div>
    </TooltipCustom>
  );
}

/**
 * Makes the "back to assanet button" for desktop
 *
 * @param handleBackToAssaNet - Called when back to assanet is requested
 * @param classes - Classes generated by useStyles
 */
export function makeBackToAssanetDesktop(
  handleBackToAssaNet: () => void,
  classes: ClassesType,
): React.ReactNode {
  return (
    <ButtonBase
      component="button"
      data-testid="back-to-MiAssaNet"
      onClick={handleBackToAssaNet}
    >
      <Fab
        size="small"
        data-testid="openFormNewQuotation"
        className={classes.fab}
      >
        <img src="/svg/back_asssanet_arrow.svg" alt="new" width={18} />
      </Fab>
      <Typography
        variant="caption"
        className={clsx(classes.appBarText, classes.toolbarItem)}
      >
        Regresar a <br />
        Mi ASSAnet
      </Typography>
      <Divider orientation="vertical" flexItem className={classes.divider} />
    </ButtonBase>
  );
}

/**
 * Makes the user welcome layout.
 *
 * @param user - The name of the logged user
 * @param classes - Classes generated by useStyles
 */
export function makeUserLayoutDesktop(
  user: string,
  classes: ClassesType,
  welcomeMessage = 'Hola',
): React.ReactNode {
  return (
    <Typography
      data-testid="desktop-user"
      variant="caption"
      className={clsx(classes.appBarText, classes.toolbarItem)}
      style={{ textAlign: 'right' }}
    >
      ¡{welcomeMessage} {user}!
    </Typography>
  );
}

/**
 * Makes the profile button (no action yet)
 *
 * @param classes - Classes generated by useStyles
 */
export function makeProfileButtonDesktop(
  handleRedirectProfile: () => void,
  classes: ClassesType,
  profileUnderstood: boolean,
  setProfileUnderstood: () => void,
): React.ReactNode {
  const role = getLoggedRole();
  return (
    <InformationPopper
      information="Encuentra aquí opciones para personalizar tus cotizaciones"
      onUnderstood={setProfileUnderstood}
      title="Nueva sección: Mi perfil"
      open={!profileUnderstood && role === 'AGE'}
      leftArrowAlign={9}
    >
      <Tooltip title="Mi perfil">
        <IconButton onClick={handleRedirectProfile}>
          <User className={classes.profileItem} color={colors.white} />
        </IconButton>
      </Tooltip>
    </InformationPopper>
  );
}

/**
 * Makes the notification button (no action yet).
 *
 * @param classes - Classes generated by useStyles
 */
export function makeNotificationButtonDesktop(
  classes: ClassesType,
): React.ReactNode {
  return (
    <Tooltip title="Notificaciones">
      <IconButton>
        <Bell className={classes.toolbarItem} color={colors.white} />
      </IconButton>
    </Tooltip>
  );
}

/**
 * Makes the logout button for the desktop
 *
 * @param handleLogout - Action called when the logout is requested
 * @param classes - Classes generated by useStyles.
 */
export function makeLogoutButtonDesktop(
  handleLogout: (comesFromAssanet: boolean) => void,
  classes: ClassesType,
): React.ReactNode {
  return (
    <Tooltip title="Cerrar sesión">
      <IconButton
        id="sales-web-button-logout"
        data-testid="logout-desktop"
        onClick={(): void => handleLogout(false)}
      >
        <LogOut className={classes.toolbarItem} color={colors.white} />
      </IconButton>
    </Tooltip>
  );
}
